import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '@/shared/api';
import type {
  TToolsListItem,
  TSelectedTool,
  TSelectedToolSave,
  ICreateToolResponse,
  IUpdateToolResponse,
  IDuplicateToolResponse,
} from '@intone/shared/types';

/** Hook to list all tools */
export function useToolsList() {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: ['tools'],
    queryFn: async () => {
      return apiClient<TToolsListItem[]>('/tools');
    },
  })
}

/** Hook to get a single tool by ID */
export function useTool(id: number | null) {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: ['tools', id],
    queryFn: async () => {
      return apiClient<TSelectedTool>(`/tools/${id}`)
    },
    enabled: id !== null && id > 0,
  })
}

/** Hook to create a new tool */
export function useCreateTool() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: TSelectedToolSave) => {
      return apiClient<ICreateToolResponse>('/tools', {
        method: 'POST',
        body: JSON.stringify(data),
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tools'] })
    },
  });
}

/** Hook to duplicate a tool */
export function useDuplicateTool() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, newName }: { id: number; newName: string }) => {
      return apiClient<IDuplicateToolResponse>(`/tools/${id}/duplicate`, {
        method: 'POST',
        body: JSON.stringify({ newName }),
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tools'] })
    },
  });
}

/** Hook to update an existing tool */
export function useUpdateTool() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: TSelectedToolSave & {  id: number }) => {
      return apiClient<IUpdateToolResponse>(`/tools/${data.id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['tools'] });
      queryClient.invalidateQueries({ queryKey: ['tools', variables.id] });
    },
  });
}

/** Hook to delete a tool */
export function useDeleteTool() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      return apiClient<{ message: string }>(`/tools/${id}`, {
        method: 'DELETE',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tools'] })
    },
  });
}

// =============================================================================
// RAG Tool File Management
// =============================================================================

interface PresignedUploadResponse {
  upload_url: string
  s3_key: string
}

interface FileUploadResponse {
  file_id: number
  openai_file_id: string
  message: string
}

/** Hook to upload a file to a RAG tool */
export function useUploadToolFile() {
  const apiClient = useApiClient();

  return useMutation({
    mutationFn: async ({ toolId, file }: { toolId: number; file: File }) => {
      // Step 1: Get presigned URL
      const { upload_url, s3_key } = await apiClient<PresignedUploadResponse>(
        `/tools/${toolId}/upload-url`,
        {
          method: 'POST',
          body: JSON.stringify({
            filename: file.name,
            content_type: file.type || 'application/octet-stream',
          }),
        }
      )

      // Step 2: Upload to S3
      const uploadResponse = await fetch(upload_url, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type || 'application/octet-stream',
        },
      })

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload file to S3')
      }

      // Step 3: Confirm upload and add to vector store
      const result = await apiClient<FileUploadResponse>(
        `/tools/${toolId}/files`,
        {
          method: 'POST',
          body: JSON.stringify({
            s3_key,
            filename: file.name,
            file_size: file.size,
            mime_type: file.type || 'application/octet-stream',
          }),
        }
      )

      return result
    },
  })
}

/** Hook to delete a file from a RAG tool */
export function useDeleteToolFile() {
  const apiClient = useApiClient();

  return useMutation({
    mutationFn: async ({ toolId, fileId }: { toolId: number; fileId: number }) => {
      return apiClient<{ message: string }>(`/tools/${toolId}/files/${fileId}`, {
        method: 'DELETE',
      })
    },
  })
}